// GrantsUsageBalance.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Card, CardBody, Row, Col, Label, Button } from "reactstrap";
import SelectField from "../Utils/SelectField";
import GrantDistributionChart from "./BarChart";
import GrantCarousel from "./GrantCarousel";
import UsageTable from "./UsageTable";
import { useNavigate } from "react-router-dom";
import DataUsageTrend from "./DataUsageTrend";

export default function GrantsUsageBalance({ data, fetchCustomerSummaryData }) {
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState("");
  const [activeType, setActiveType] = useState("base"); // "base" or "addon"
  const navigate = useNavigate();
  const subscriptions =
    data && data.subscriptionList ? data.subscriptionList : [];

  const uniqueSubscriptionIds = useMemo(() => {
    if (fetchCustomerSummaryData?.resources) {
      const subscriptionIds = [
        ...new Set(
          fetchCustomerSummaryData?.resources
            ?.filter(
              (resource) => !resource.isAggregate && resource.subscriptionId
            )
            ?.map((resource) => resource.subscriptionId)
        ),
      ];
      return subscriptionIds;
    } else if (subscriptions) {
      const subscriptionIds = [
        ...new Set((subscriptions || [])?.map((sub) => sub.subscriptionId)),
      ];
      return subscriptionIds;
    } else return [];
  }, [fetchCustomerSummaryData, data]);

  const chargedResource = useMemo(() => {
    if (fetchCustomerSummaryData?.resources?.length > 0) {
      const chargeResource =
        fetchCustomerSummaryData?.resources?.filter(
          (resource) => resource.currencyCode
        ) || [];
      return chargeResource;
    } else return [];
  }, [fetchCustomerSummaryData?.resources]);

  const filteredResources = useMemo(() => {
    if (!selectedSubscriptionId) return [];
    return fetchCustomerSummaryData?.resources?.filter(
      (item) =>
        item?.subscriptionId === selectedSubscriptionId &&
        item?.isAddOn === (activeType === "addon" ? true : false)
    );
  }, [fetchCustomerSummaryData?.resources, selectedSubscriptionId, activeType]);

  const handleSubscriptionChange = (selectedOption) => {
    setSelectedSubscriptionId(selectedOption ? selectedOption.value : "");
    // setActiveIndex(0);
    // setChartKey(prev => prev + 1);
  };

  useEffect(() => {
    if (uniqueSubscriptionIds.length > 0) {
      if (
        !selectedSubscriptionId ||
        !uniqueSubscriptionIds.includes(selectedSubscriptionId)
      ) {
        setSelectedSubscriptionId(uniqueSubscriptionIds[0]);
        // setActiveIndex(0);
        // setChartKey(prev => prev + 1);
      }
    } else {
      setSelectedSubscriptionId("");
    }
  }, [uniqueSubscriptionIds, selectedSubscriptionId]);

  return (
    <div className="grants-usage-wrapper">
      <Row>
        <Col lg={12} md={12} sm={12} className="mb-3">
          <Card className="panel-card">
            <CardBody className="px-4">
              <div className="top-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-3">
                <h5 className="customer-title mb-0">Grants & Usage Balance</h5>

                <div
                  className="d-flex align-items-center"
                  style={{ width: "100%", maxWidth: "400px" }}
                >
                  <i
                    className="bx bx-package me-2"
                    style={{
                      fontSize: 20,
                      color: "#FD7545",
                      height: 40,
                      width: 40,
                      borderRadius: "50%",
                      backgroundColor: "#FD75451A",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  ></i>
                  <Label
                    for="subscriptionSelect"
                    className="mb-0 me-2 text-nowrap"
                  >
                    Subscription:
                  </Label>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <SelectField
                      id="subscriptionSelect"
                      value={
                        selectedSubscriptionId
                          ? {
                              value: selectedSubscriptionId,
                              label: selectedSubscriptionId,
                            }
                          : null
                      }
                      placeholder="Select Subscription ID"
                      handleChange={handleSubscriptionChange}
                      options={uniqueSubscriptionIds?.map((id) => ({
                        value: id,
                        label: id,
                      }))}
                      isMulti={false}
                    />
                  </div>
                </div>
              </div>
              <div className="top-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center my-4 gap-3">
                {/* Left: Base Offers / Add-ons Toggle */}
                <div className="offer-toggle">
                  <button
                    className={`toggle-btn ${
                      activeType === "base" ? "active" : ""
                    }`}
                    onClick={() => setActiveType("base")}
                  >
                    Base Offers
                  </button>

                  <button
                    className={`toggle-btn ${
                      activeType === "addon" ? "active" : ""
                    }`}
                    onClick={() => setActiveType("addon")}
                  >
                    Add-ons
                  </button>
                </div>

                {/* Right: Balance Display */}
                {chargedResource?.[0] && (
                  <div style={{ minWidth: "400px" }}>
                    <div>
                      <span className="funds-currency me-2">
                        {chargedResource?.[0]?.currencyCode}
                      </span>
                      <span className="funds-amount">
                        {chargedResource[0]?.remainingBalance < 0
                          ? `-${Math.abs(chargedResource[0]?.remainingBalance)}`
                          : `${chargedResource[0]?.remainingBalance}`}
                      </span>
                    </div>
                    <div className="funds-label">
                      {chargedResource[0]?.type == "Prepaid"
                        ? "Available funds"
                        : "Balance Due"}
                    </div>
                  </div>
                )}
              </div>
              <h5 className="customer-title" style={{ color: "#0F172B" }}>
                Grants Overview
              </h5>

              <Row>
                <Col md={6} sm={12} className="mb-3 mt-2">
                  <GrantDistributionChart
                    activeType={activeType}
                    filteredResources={filteredResources?.filter(
                      (item) => !item?.isCounter
                    )}
                  />
                </Col>

                <Col md={6} sm={12} className="mb-3 mt-2">
                  <GrantCarousel
                    filteredResources={filteredResources?.filter(
                      (item) => !item?.isCounter
                    )}
                  />
                </Col>
              </Row>

              <h5 className="customer-title" style={{ color: "#0F172B" }}>
                Usage Details
              </h5>

              <Row>
                <Col md={6} sm={12} className="mb-3 mt-2">
                  <DataUsageTrend />
                </Col>

                <Col md={6} sm={12} className="mb-3 mt-2">
                  <UsageTable filteredResources={filteredResources} />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <div className="d-flex justify-content-center align-items-center gap-2">
        <Button
          color="secondary"
          onClick={() => {
            navigate(`/selfcare-changeplan/${selectedSubscriptionId}`);
          }}
          className="d-flex justify-content-center align-items-center"
        >
          <i className="bx bx-cart fs-4 me-2"></i>
          <span>Change Plan</span>
        </Button>
        <Button
          color="#8B5CF6"
          style={{ backgroundColor: "#8B5CF6", color: "white", border: "none" }}
          onClick={() => {
            navigate(`/selfcare-addon/${selectedSubscriptionId}`);
          }}
          className="d-flex justify-content-center align-items-center"
        >
          <i className="bx bx-plus fs-4 me-2"></i>
          <span>Purchase Add-on</span>
        </Button>
      </div>
    </div>
  );
}
